import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  RouterModule,
  NavigationEnd,
  Router,
  ActivatedRoute,
} from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import dayjs from 'dayjs';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { assets } from '../../../core/constants/assets.const';
import { User } from '../../../models/auth';
import { countryResponse } from '../../../models/countryCurrency.model';
import { enquiry, enquiryResponse } from '../../../models/enquiries';
import { CountryService } from '../../../services/countryCurrency.service';
import { EncryptionService } from '../../../services/encryption.service';
import { EnquiryService } from '../../../services/enquiries.service';
import { GeoLocationService } from '../../../services/geo-location-service.service';
import { SocketsService } from '../../../services/sockets.service';
import isToday from 'dayjs/plugin/isToday';
import { EnquiryCardComponent } from '../enquiry-card/enquiry-card.component';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';

dayjs.extend(isToday);

@Component({
  selector: 'logibids-driver-enquiry-immediate',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    RouterModule,
    InfiniteScrollModule,
    FormsModule,
    NgxUiLoaderModule,
    EnquiryCardComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: './driver-enquiry-immediate.component.html',
  styleUrl: './driver-enquiry-immediate.component.scss',
})
export class DriverEnquiryImmediateComponent implements OnInit {
  protected readonly search = signal<string>('');
  protected readonly size = signal<number>(10);
  protected readonly from = signal<number>(1);
  protected readonly total = signal<number>(0);
  protected readonly assetsConst = assets;
  protected readonly auctions = signal<enquiry[]>([]);

  protected userProfile: User;
  protected currencyRate = signal('1');
  protected auctionType = signal('');
  protected isTrackingRoute = signal(false);
  protected genderPrefId = 2;

  private readonly locationService = inject(GeoLocationService);
  private readonly enquiryService = inject(EnquiryService);
  private readonly socketsService = inject(SocketsService);
  private readonly encryption = inject(EncryptionService);
  private readonly countryService = inject(CountryService);
  private readonly ngxLoader = inject(NgxUiLoaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(I18nService);
  private auctionsLoaded = signal(false);
  protected readonly isLoading = signal(true);

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.isTrackingRoute.set(
            event.urlAfterRedirects.startsWith('/sp-bids/tracking/')
          );
        }
      });

    this.userProfile = this.encryption.decryptLocalStorage('profile');

    if (this.userProfile) {
      this.route.data
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          this.auctionType.set(data['auctionType'] || '');
        });

      this.getCurrencyRate();

      effect(() => {
        const current = this.locationService.currentCoords();
        if (current && current.latitude > 0 && current.longitude > 0) {
          this.socketsService.sendPosition({
            lat: current.latitude,
            long: current.longitude,
          });
        }
      });

      effect(() => {
        const data: any = this.enquiryService.driverEnquiryImmediate();
        if (data && data.showList === true) {
          this.isLoading.set(true);
          if (data.enquiry) {
            this.getEnquiry(data.enquiry.id);
          } else if (data.all === true && !this.auctionsLoaded()) {
            this.auctionsLoaded.set(true);
            this.getAuctions();
          } else {
            this.isLoading.set(false);
          }
        } else {
          this.isLoading.set(false);
        }
      });
    }
  }

  ngOnInit() {}

  private getCurrencyRate(): void {
    this.countryService
      .getCountryCurrencyRate(this.userProfile.country_code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: countryResponse) => {
        this.currencyRate.set(res.curr_rate_kd);
      });
  }

  private getEnquiry(enquiryId: string) {
    this.enquiryService
      .getEnqueryById(enquiryId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: enquiry) => {
          const item = this.transformToEnquiry(res);
          this.auctions.set([item]);
          this.isLoading.set(false);

          const showTime = this.userProfile?.default_region?.se_show_time ?? 30;
          const intervalMs = showTime * 1000;
          setTimeout(() => {
            this.enquiryService.setDriverSOcketDataImmedidate({
              enquiry: [],
              showList: false,
            });
          }, intervalMs);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  protected getAuctions() {
    let query = `from=${this.from()}&size=${this.size()}&schedule_type=SCHEDULE&status=RUNNING`;
    if (this.search()) {
      query += `&search=${this.search()}`;
    }

    this.enquiryService
      .getEnqueries(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: enquiryResponse) => {
          const newItems = res.list.map(item => this.transformToEnquiry(item));
          this.auctions.update(prev => [...prev, ...newItems]);
          this.total.set(res.total);
          this.from.update(f => f + 1);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  private transformToEnquiry(item: enquiry): enquiry {
    return new enquiry(
      {
        ...item,
        currencyRate: this.currencyRate(),
        userCurrency: this.userProfile.currency_code,
        isActiveHeight: true,
        ispreferences: false,
      },
      this.translateService,
      this.genderPrefId
    );
  }

  protected onScrollingFinished() {
    if (this.auctions().length >= this.total()) return;
    this.getAuctions();
  }

  protected handleBidPlaced() {
    this.enquiryService.setDriverSOcketDataImmedidate({
      enquiry: [],
      showList: false,
    });
  }

  protected handleAuctionAccepted() {
    this.enquiryService.setDriverSOcketDataImmedidate({
      enquiry: [],
      showList: false,
    });
  }

  protected skip(): void {
    this.enquiryService.setDriverSOcketDataImmedidate({
      enquiry: [],
      showList: false,
    });
  }
}
